import { ReadDepartmentRecordsChannel } from '@/services/api/generated/policeDepartmentsService';

/**
 * Format phone/fax number to (XXX) XXX-XXXX
 */
export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');

  // Handle +1XXXXXXXXXX format (11 digits starting with 1)
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    const num = cleaned.slice(1);
    return `(${num.slice(0, 3)}) ${num.slice(3, 6)}-${num.slice(6)}`;
  }

  // Handle XXXXXXXXXX format (10 digits)
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  // Handle 7-digit local numbers
  if (cleaned.length === 7) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  }

  return phone;
};

/**
 * Format address to single line from address object
 */
export const formatAddressFromObject = (address: {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  province_or_state?: string | null;
  state?: string | null;
  postal_or_zip?: string | null;
  zip?: string | null;
}): string | null => {
  const parts = [
    address.address_line1,
    address.address_line2,
    address.city,
    address.province_or_state || address.state,
    address.postal_or_zip || address.zip,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : null;
};

/**
 * Format address to single line (legacy helper)
 */
export const formatAddress = (
  addressLine1?: string | null,
  city?: string | null,
  state?: string | null,
  zip?: string | null,
): string | null => {
  const parts = [addressLine1, city, state, zip].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
};

/**
 * Format contact value based on type
 * Priority for MAILING_ADDRESS: mailing_instructions.address > handle fields
 */
export const formatContactValue = (
  channel: ReadDepartmentRecordsChannel,
): string | null => {
  const { handle, mailing_instructions } = channel;
  if (!handle) return null;

  const handleType = handle.handle_type?.toUpperCase();

  // For mailing address, prioritize mailing_instructions.address
  if (handleType === 'MAILING_ADDRESS') {
    if (mailing_instructions?.address) {
      return formatAddressFromObject(mailing_instructions.address);
    }
    return formatAddress(
      handle.address_line1,
      handle.city,
      handle.state,
      handle.zip,
    );
  }

  // Format phone/fax numbers
  if ((handleType === 'PHONE' || handleType === 'FAX') && handle.value) {
    return formatPhoneNumber(handle.value);
  }

  return handle.value || null;
};

/**
 * Format fee amount in cents to dollar string
 */
export const formatFeeAmount = (cents: number | null | undefined): string => {
  if (!cents) return 'Free';
  const dollars = cents / 100;
  return `$${dollars.toFixed(2)}`;
};

/**
 * Get estimated fee from channel (priority: fee_amount > mailing fee structure)
 */
export const getEstimatedFee = (
  channel: ReadDepartmentRecordsChannel,
): number | null => {
  return (
    channel.mailing_instructions?.fee_structure?.estimated_fee_cents ?? null
  );
};

/**
 * Extract hostname from URL
 */
export const extractHostname = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

/**
 * Format services provided for display
 */
export const formatServicesProvided = (
  services: string[] | null | undefined,
): { display: string; full: string } | null => {
  if (!services || services.length === 0) return null;

  // Clean up service names: remove underscores, capitalize
  const cleanServices = services.map((s) =>
    s
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '),
  );

  const full = cleanServices.join(', ');

  // Show first 2, then +N
  if (cleanServices.length <= 2) {
    return { display: full, full };
  }

  const visible = cleanServices.slice(0, 2).join(', ');
  const remaining = cleanServices.length - 2;
  return {
    display: `${visible} +${remaining}`,
    full,
  };
};

/**
 * Mail requirement display labels
 */
export const MAIL_REQUIREMENT_LABELS: Record<string, string> = {
  CLIENT_PHOTO_ID_COPY: 'Copy of photo ID',
  FIRM_LETTERHEAD: 'Firm letterhead',
  PROOF_OF_REPRESENTATION: 'Proof of representation',
  COMPLETED_REQUEST_FORM: 'Completed request form',
  NOTARIZED_REQUEST_FORM: 'Notarized request form',
};
